import type { CasinoDomain, CasinoDomainStatus, CasinoDomainType, DnsRecord } from '../../mock-data/types';
import type { DnsRecordType } from '../../theme/tokens';

/**
 * Adapter: raw `domain-info` API payload → the `CasinoDomain[]` view shape the
 * Website Domain section renders.
 *
 * This is the SINGLE place that interprets the `/casino-instance/domain-info`
 * response (the slice stores it raw as `unknown`), so confirming/changing the API
 * shape only touches this file.
 *
 * ⚠️ PROVISIONAL: the success payload could not be observed on the test backend
 * (every available brand returns `DOMAIN_NOT_FOUND`, and the one brand with a live
 * domain hits an upstream 422). So the parsing is deliberately tolerant of the
 * field names a domain/registrar record plausibly uses. Confirm against a real
 * response and prune the alternatives. See docs/api.owners-hub.domain-info.md.
 *
 * Returns `null` when the payload carries no usable domain record, so the caller
 * can fall back to its placeholder rather than render an empty section.
 */

/* ---------- small, no-`any` accessors over an unknown payload ---------- */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** First defined value among the given keys of a record. */
const pick = (record: Record<string, unknown>, ...keys: string[]): unknown => {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null) return record[key];
  }
  return undefined;
};

const str = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
};

const num = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return undefined;
};

const bool = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (/^(true|yes|1)$/i.test(value)) return true;
    if (/^(false|no|0)$/i.test(value)) return false;
  }
  return undefined;
};

/** Format an ISO date / epoch into the section's short label ("Mar 14, 2027"). */
const dateLabel = (value: unknown): string | undefined => {
  const raw = str(value);
  if (!raw) return undefined;
  // Accept both ISO strings and epoch seconds/millis.
  const epoch = num(value);
  const date = epoch !== undefined ? new Date(epoch < 1e12 ? epoch * 1000 : epoch) : new Date(raw);
  if (Number.isNaN(date.getTime())) return raw; // already a human label, pass through
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/* ---------- field mappers ---------- */

const DNS_TYPES: readonly DnsRecordType[] = ['A', 'NS', 'MX', 'CNAME', 'TXT'];

const toDnsType = (value: unknown): DnsRecordType => {
  const upper = (str(value) ?? '').toUpperCase();
  return (DNS_TYPES as readonly string[]).includes(upper) ? (upper as DnsRecordType) : 'A';
};

const toDnsRecords = (value: unknown): DnsRecord[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const records = value
    .map((entry): DnsRecord | undefined => {
      if (!isRecord(entry)) return undefined;
      const host = str(pick(entry, 'host', 'name', 'hostname'));
      const recordValue = str(pick(entry, 'value', 'data', 'content', 'target', 'points_to'));
      if (host === undefined && recordValue === undefined) return undefined;
      return {
        type: toDnsType(pick(entry, 'type', 'record_type')),
        host: host ?? '@',
        value: recordValue ?? '',
        ttl: num(pick(entry, 'ttl', 'time_to_live')) ?? 3600,
      };
    })
    .filter((record): record is DnsRecord => record !== undefined);
  return records.length > 0 ? records : undefined;
};

const toStatus = (value: unknown, hasUnsetDns: boolean): CasinoDomainStatus => {
  const status = (str(value) ?? '').toLowerCase().replace(/[\s_]+/g, '-');
  if (/(active|connected|live|verified|ready|ok)/.test(status)) return 'active';
  if (/(pending|provisioning|processing|in-progress|setup|registering)/.test(status)) return 'pending';
  if (/(action-required|action|error|failed|misconfigured|unverified|warning|attention)/.test(status)) {
    return 'action-required';
  }
  // No explicit status: an unmanaged domain whose DNS still needs setting reads as
  // "action required"; otherwise treat the record as live.
  return hasUnsetDns ? 'action-required' : 'active';
};

const toType = (value: unknown, managedFlag: boolean | undefined, hasDns: boolean): CasinoDomainType => {
  const type = (str(value) ?? '').toLowerCase();
  if (type.includes('manage') && !type.includes('un')) return 'managed';
  if (type.includes('unmanage') || type.includes('external') || type.includes('byo') || type.includes('connect')) {
    return 'unmanaged';
  }
  if (managedFlag !== undefined) return managedFlag ? 'managed' : 'unmanaged';
  // No explicit type/flag: a record carrying owner-facing DNS records is an
  // owner-connected (unmanaged) domain; a bare record is one we manage.
  return hasDns ? 'unmanaged' : 'managed';
};

/** Map one raw domain record → CasinoDomain, or `undefined` if it has no address. */
const toDomain = (record: Record<string, unknown>, index: number, liveDomain?: string): CasinoDomain | undefined => {
  const name = str(pick(record, 'name', 'domain', 'domain_name', 'host', 'url', 'address')) ?? liveDomain;
  if (!name) return undefined;

  const dns = toDnsRecords(pick(record, 'dns', 'dns_records', 'records', 'dnsRecords'));
  const managedFlag = bool(pick(record, 'managed', 'is_managed', 'managed_by_whitelabels'));
  const type = toType(pick(record, 'type', 'domain_type', 'kind'), managedFlag, dns !== undefined);
  const status = toStatus(pick(record, 'status', 'state', 'connection_status'), type === 'unmanaged' && dns === undefined);

  const renewsOn = dateLabel(pick(record, 'renews_on', 'renewal_date', 'expires_at', 'expiry', 'expiry_date', 'renews_at'));
  const connectedOn = dateLabel(pick(record, 'connected_on', 'connected_at', 'created_at', 'verified_at', 'added_at'));

  return {
    id: str(pick(record, 'id', 'domain_id', '_id', 'uuid')) ?? `${name}-${index}`,
    name,
    type,
    primary: bool(pick(record, 'primary', 'is_primary', 'default', 'is_default')) ?? true,
    status,
    registrar:
      str(pick(record, 'registrar', 'registrar_name', 'provider', 'dns_provider')) ??
      (type === 'managed' ? 'Whitelabels Registrar' : ''),
    // The section only shows the relevant one per type, but fill whatever the
    // record carries so a mistyped record still has a sensible date.
    ...(renewsOn ? { renewsOn } : {}),
    ...(connectedOn ? { connectedOn } : {}),
    ...(dns ? { dns } : {}),
  };
};

/** Unwrap the payload to the array (or single record) of domains it carries. */
const toRecordList = (payload: unknown): Record<string, unknown>[] => {
  if (Array.isArray(payload)) return payload.filter(isRecord);
  if (!isRecord(payload)) return [];
  // A `{ data | domains | result | … }` envelope, or the record itself.
  const nested = pick(payload, 'domains', 'data', 'result', 'items', 'records', 'domain');
  if (Array.isArray(nested)) return nested.filter(isRecord);
  if (isRecord(nested)) return [nested];
  // The payload IS a single domain record (has an address field).
  if (pick(payload, 'name', 'domain', 'domain_name', 'host', 'url', 'address') !== undefined) return [payload];
  return [];
};

/**
 * Build the section's `CasinoDomain[]` from the raw `domain-info` payload.
 * `liveDomain` (the brand's `casino_data.details.domain`) backfills the display
 * name when the record itself omits one. Returns `null` when nothing usable is
 * present, so the caller falls back to its placeholder.
 */
export const resolveCasinoDomains = (payload: unknown, liveDomain?: string): CasinoDomain[] | null => {
  if (payload === undefined || payload === null) return null;
  const domains = toRecordList(payload)
    .map((record, index) => toDomain(record, index, liveDomain))
    .filter((domain): domain is CasinoDomain => domain !== undefined);
  if (domains.length === 0) return null;
  // Exactly one primary: keep the first flagged primary, demote the rest; if none
  // flagged, the first becomes primary so the "Visit site" link has an anchor.
  const primaryIndex = Math.max(0, domains.findIndex((domain) => domain.primary));
  return domains.map((domain, index) => ({ ...domain, primary: index === primaryIndex }));
};

export default resolveCasinoDomains;
